/** « Calibrage » (P3B-05): the AI's marks against the trainers' re-grading, for EE and EO. The
 * objective (minimum copies, maximum mean gap) comes from the TCF format table. */

import { type CalibrationCriterion, type CalibrationSkill, useAcademyCalibrationRetrieve } from "@agencyos/api-client";
import { Badge, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useTranslation } from "react-i18next";

import { useAdminExam, useExamTexts } from "./examContext";

const SKILLS = ["ee", "eo"] as const;

export function localizedLabel(label: Record<string, string> | undefined, language: string, fallback: string): string {
  if (!label) return fallback;
  return label[language.slice(0, 2)] ?? label.fr ?? fallback;
}

/** Signed gap with its direction (AI minus trainer: + when the AI gives more). */
export function signed(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined) return "–";
  const text = Math.abs(value).toLocaleString(locale, { maximumFractionDigits: 2 });
  return value > 0 ? `+${text}` : value < 0 ? `−${text}` : text;
}

function SkillCard({
  skill,
  data,
  minCopies,
  maxGap,
}: {
  skill: (typeof SKILLS)[number];
  data: CalibrationSkill;
  minCopies: number;
  maxGap: number;
}) {
  const { t, i18n } = useTranslation();
  const { ielts, tef, goethe } = useAdminExam();
  const texts = useExamTexts();
  const locale = i18n.language.startsWith("en") ? "en-GB" : "fr-FR";
  const decimal = (value: number | null | undefined) =>
    value === null || value === undefined ? "–" : value.toLocaleString(locale, { maximumFractionDigits: 2 });
  const tendency = data.tendency || "none";
  return (
    <section className="installment-row stack" aria-labelledby={`calibration-${skill}`}>
      <div className="row between">
        <h3 id={`calibration-${skill}`}>{texts.skill(skill)}</h3>
        <Badge tone={data.reached ? "success" : "offer"}>
          {data.reached ? t("academyCalibration.reached") : t("academyCalibration.notReached")}
        </Badge>
      </div>
      <dl className="stack">
        <div className="row between">
          <dt>{t("academyCalibration.copies")}</dt>
          <dd>
            <strong>{t("academyCalibration.copiesOf", { n: data.copies, min: minCopies })}</strong>
          </dd>
        </div>
        <div className="row between">
          <dt>{t("academyCalibration.meanGap")}</dt>
          <dd>
            <strong>
              {ielts
                ? t("academyAdmin.ielts.calibration.gapBands", { value: decimal(data.mean_gap) })
                : tef
                  ? t("tef.admin.calibration.gap", { value: decimal(data.mean_gap) })
                  : goethe
                  ? t("goethe.admin.calibration.gap", { value: decimal(data.mean_gap) })
                  : t("academyCalibration.outOf20", { value: decimal(data.mean_gap) })}
            </strong>
          </dd>
        </div>
        <div className="row between">
          <dt>{t("academyCalibration.tendency")}</dt>
          <dd>
            {t(`academyCalibration.trend.${tendency}`)} ({signed(data.bias, locale)})
          </dd>
        </div>
      </dl>
      <p className="muted academy-small">
        {ielts
          ? t("academyAdmin.ielts.calibration.objective", { gap: decimal(maxGap), min: minCopies })
          : tef
            ? t("tef.admin.calibration.objective", { gap: decimal(maxGap), min: minCopies })
            : goethe
              ? t("goethe.admin.calibration.objective", { gap: decimal(maxGap), min: minCopies })
              : t("academyCalibration.objective", { gap: decimal(maxGap), min: minCopies })}
      </p>
      {data.copies > 0 ? (
        <div className="table-wrap">
        <table className="table">
          <caption className="muted">{t("academyCalibration.byCriterion")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("academyCalibration.criterion")}</th>
              <th scope="col">{t("academyCalibration.gap")}</th>
              <th scope="col">{t("academyCalibration.tendency")}</th>
            </tr>
          </thead>
          <tbody>
            {data.criteria.map((c: CalibrationCriterion) => (
              <tr key={c.key}>
                <td>
                  {localizedLabel(c.label, i18n.language, c.key)}{" "}
                  <span className="muted">{t("academyCalibration.max", { max: c.max })}</span>
                </td>
                <td>{decimal(c.mean_gap)}</td>
                <td>
                  {t(`academyCalibration.trend.${c.tendency || "none"}`)} ({signed(c.bias, locale)})
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <p className="muted">{t("academyCalibration.empty")}</p>
      )}
    </section>
  );
}

export function CalibrationTab() {
  const { t } = useTranslation();
  const { ielts, tef, goethe, params } = useAdminExam();
  const query = useAcademyCalibrationRetrieve(params);
  const data = query.data?.status === 200 ? query.data.data : undefined;
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("academyCalibration.title")}</h2>
          <p className="muted">{ielts ? t("academyAdmin.ielts.calibration.intro") : tef ? t("tef.admin.calibration.intro") : goethe ? t("goethe.admin.calibration.intro") : t("academyCalibration.intro")}</p>
        </div>
      </div>
      {query.isError ? (
        <p className="ko" role="alert">
          {t("academyCalibration.error")}
        </p>
      ) : data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : (
        <div className="grid-2">
          {SKILLS.map((skill) => (
            <SkillCard key={skill} skill={skill} data={data.skills[skill]} minCopies={data.min_copies} maxGap={data.max_mean_gap} />
          ))}
        </div>
      )}
      <p className="muted">{t("academyCalibration.rule")}</p>
    </GlassCard>
  );
}
